import {
  type ConsentChannel,
  type ConsentDecision,
  type ConsentRecord,
  type CustomerProfile,
  type CustomerStatus,
  type IysStatus,
  MESSAGE_CHANNELS,
  type MessageChannel,
  PRIVACY_CONSENT_PURPOSE_LABELS,
  type PrivacyText,
  type PrivacyTextKind,
  type Vehicle,
  type VehicleConnectorType,
} from "@station/contracts";
import { addDays, Decimal, isValidTrPlate, type LocalDate, Money, normalizePhone } from "@station/core";
import type { Tone } from "@station/ui";
import { z } from "zod";
import {
  type ChartOption,
  formatChartNumber,
  GRID,
  LEGEND_POSITION,
  VALUE_AXIS_LABEL,
} from "../fuel/chart-options.js";
import { phoneInputToApi } from "./personal-data-values.js";

/**
 * Müşteri ekranlarının SAF yardımcıları (Prompt 17 web) — birim testli. Para hesabı `Money` / `Decimal` ile yapılır;
 * grafik için sayıya çevirme yalnızca ÇİZİM anındadır (AGENTS.md §3.2'nin izin verdiği tek yer).
 */

// ── Rozet tonları ────────────────────────────────────────────────────────────
export const CUSTOMER_STATUS_TONE: Record<CustomerStatus, Tone> = {
  ACTIVE: "success",
  INACTIVE: "neutral",
  ANONYMIZED: "warning",
};

/** İYS durumu: gönderilecek bilgi, İYS'de başarı, hata tehlike, gerekmez nötr (anlamı rozet METNİ de taşır). */
export const IYS_STATUS_TONE: Record<IysStatus, Tone> = {
  PENDING: "info",
  SYNCED: "success",
  FAILED: "danger",
  NOT_REQUIRED: "neutral",
};

export function iysTone(status: IysStatus | null): Tone {
  return status === null ? "neutral" : IYS_STATUS_TONE[status];
}

// ── Liste sorgusu ────────────────────────────────────────────────────────────
export const CUSTOMER_SEARCH_MIN_LENGTH = 2;

export interface CustomerListFilters {
  q?: string | undefined;
  status?: CustomerStatus | undefined;
  homeStationId?: string | undefined;
}

/** Arama metni API'ye yalnızca en az 2 karakterse gider (sunucu kısa aramayı 400 ile reddeder). */
export function isSearchable(text: string | undefined): boolean {
  return (text ?? "").trim().length >= CUSTOMER_SEARCH_MIN_LENGTH;
}

export function customerListQuery(
  filters: CustomerListFilters,
  cursor: string | undefined,
  limit = 25,
): { limit: number; q?: string; status?: CustomerStatus; homeStationId?: string; cursor?: string } {
  return {
    limit,
    ...(isSearchable(filters.q) ? { q: filters.q!.trim() } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.homeStationId ? { homeStationId: filters.homeStationId } : {}),
    ...(cursor ? { cursor } : {}),
  };
}

// ── Profil formu ─────────────────────────────────────────────────────────────
export interface CustomerFormValues {
  fullName: string;
  phone: string;
  email: string;
  homeStationId: string;
  notes: string;
  issueLoyaltyCard: boolean;
}

export function emptyCustomerForm(): CustomerFormValues {
  return { fullName: "", phone: "", email: "", homeStationId: "", notes: "", issueLoyaltyCard: true };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Profil formu doğrulaması. `masked` → kullanıcı kişisel verileri göremiyor: kişisel alanlar kilitli ve BOŞ gelir
 * (`editableValue`), bu yüzden "ad ya da telefon" koşulu aranmaz (alanlar gövdeye de çıkmaz).
 */
export function customerFormSchema(masked = false) {
  return z
    .object({
      fullName: z.string().trim().max(160, "Ad soyad en çok 160 karakter olabilir"),
      phone: z.string().trim().max(30, "Telefon en çok 30 karakter olabilir"),
      email: z.string().trim().max(254, "E-posta en çok 254 karakter olabilir"),
      homeStationId: z.string(),
      notes: z.string().trim().max(1000, "Not en çok 1000 karakter olabilir"),
      issueLoyaltyCard: z.boolean(),
    })
    .superRefine((value, context) => {
      if (masked) return;
      if (value.fullName !== "" && value.fullName.length < 2)
        context.addIssue({
          code: "custom",
          path: ["fullName"],
          message: "Ad soyad en az 2 karakter olmalıdır",
        });
      if (value.fullName === "" && value.phone === "")
        context.addIssue({
          code: "custom",
          path: ["fullName"],
          message: "Ad soyad ya da telefon girilmelidir",
        });
      if (value.phone !== "" && normalizePhone(value.phone) === null)
        context.addIssue({
          code: "custom",
          path: ["phone"],
          message: "Telefon numarası geçersiz (ör. 0532 123 45 67)",
        });
      if (value.email !== "" && !EMAIL_PATTERN.test(value.email))
        context.addIssue({ code: "custom", path: ["email"], message: "E-posta adresi geçersiz" });
    });
}

const orNull = (text: string): string | null => (text.trim() === "" ? null : text.trim());

export interface CreateCustomerBody {
  fullName?: string;
  phone?: string;
  email?: string;
  homeStationId?: string;
  notes?: string;
  createdVia: "WEB";
  issueLoyaltyCard: boolean;
}

/** Yeni müşteri gövdesi: BOŞ alan gövdeye çıkmaz (sunucu varsayılanı `null`); telefon E.164'e çevrilir. */
export function createCustomerBody(values: CustomerFormValues): CreateCustomerBody {
  const fullName = orNull(values.fullName);
  const phone = phoneInputToApi(values.phone);
  const email = orNull(values.email);
  const notes = orNull(values.notes);
  return {
    ...(fullName ? { fullName } : {}),
    ...(phone ? { phone } : {}),
    ...(email ? { email } : {}),
    ...(values.homeStationId ? { homeStationId: values.homeStationId } : {}),
    ...(notes ? { notes } : {}),
    createdVia: "WEB",
    issueLoyaltyCard: values.issueLoyaltyCard,
  };
}

export interface UpdateCustomerBody {
  version: number;
  fullName?: string | null;
  phone?: string | null;
  email?: string | null;
  homeStationId?: string | null;
  notes?: string | null;
}

/**
 * Düzenleme gövdesi: yalnızca DEĞİŞEN alanlar (+ iyimser kilit `version`). Maskeli yanıtta kişisel alanlar gövdeye
 * HİÇ çıkmaz — formda boş duran maskeli değer gerçek veriyi silmesin.
 */
export function updateCustomerBody(
  values: CustomerFormValues,
  customer: CustomerProfile,
): UpdateCustomerBody {
  const body: UpdateCustomerBody = { version: customer.version };
  if (!customer.personalDataMasked) {
    const fullName = orNull(values.fullName);
    if (fullName !== customer.fullName) body.fullName = fullName;
    const phone = phoneInputToApi(values.phone);
    if (phone !== customer.phone) body.phone = phone;
    const email = orNull(values.email);
    if (email !== customer.email) body.email = email;
    const notes = orNull(values.notes);
    if (notes !== customer.notes) body.notes = notes;
  }
  const homeStationId = values.homeStationId === "" ? null : values.homeStationId;
  if (homeStationId !== customer.homeStationId) body.homeStationId = homeStationId;
  return body;
}

// ── Araç formu ───────────────────────────────────────────────────────────────
export interface VehicleFormValues {
  plate: string;
  fuelProductId: string;
  connectorType: VehicleConnectorType | "";
  brand: string;
  model: string;
  modelYear: string;
  notes: string;
}

export function vehicleFormFrom(vehicle: Vehicle | null): VehicleFormValues {
  if (vehicle === null)
    return {
      plate: "",
      fuelProductId: "",
      connectorType: "",
      brand: "",
      model: "",
      modelYear: "",
      notes: "",
    };
  return {
    // Maskeli plaka forma YAZILMAZ (kilitli alan boş gelir).
    plate: vehicle.personalDataMasked || vehicle.plate.includes("*") ? "" : vehicle.plate,
    fuelProductId: vehicle.fuelProductId ?? "",
    connectorType: vehicle.connectorType ?? "",
    brand: vehicle.brand ?? "",
    model: vehicle.model ?? "",
    modelYear: vehicle.modelYear === null ? "" : String(vehicle.modelYear),
    notes: vehicle.notes ?? "",
  };
}

/** Model yılı: boş → `null`; 4 haneli 1950–2100 tam sayı; aksi `undefined` (geçersiz). */
export function parseModelYear(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  if (!/^\d{4}$/.test(trimmed)) return undefined;
  const year = Number.parseInt(trimmed, 10);
  return year >= 1950 && year <= 2100 ? year : undefined;
}

export type VehicleFormErrors = Partial<Record<keyof VehicleFormValues, string>>;

export function vehicleFormErrors(values: VehicleFormValues, plateLocked: boolean): VehicleFormErrors {
  const errors: VehicleFormErrors = {};
  if (!plateLocked) {
    if (values.plate.trim() === "") errors.plate = "Plaka zorunludur";
    else if (!isValidTrPlate(values.plate)) errors.plate = "Geçersiz plaka (ör. 34 ABC 123)";
  }
  if (parseModelYear(values.modelYear) === undefined)
    errors.modelYear = "Model yılı 1950–2100 arası 4 haneli olmalıdır";
  if (values.brand.trim().length > 60) errors.brand = "Marka en çok 60 karakter olabilir";
  if (values.model.trim().length > 60) errors.model = "Model en çok 60 karakter olabilir";
  if (values.notes.trim().length > 500) errors.notes = "Not en çok 500 karakter olabilir";
  return errors;
}

export interface CreateVehicleBody {
  plate: string;
  fuelProductId?: string;
  connectorType?: VehicleConnectorType;
  brand?: string;
  model?: string;
  modelYear?: number;
  notes?: string;
}

/** Yeni araç gövdesi: boş alan gövdeye çıkmaz. Çağıran önce `vehicleFormErrors` ile doğrular. */
export function createVehicleBody(values: VehicleFormValues): CreateVehicleBody {
  const year = parseModelYear(values.modelYear);
  const brand = orNull(values.brand);
  const model = orNull(values.model);
  const notes = orNull(values.notes);
  return {
    plate: values.plate.trim(),
    ...(values.fuelProductId ? { fuelProductId: values.fuelProductId } : {}),
    ...(values.connectorType ? { connectorType: values.connectorType } : {}),
    ...(brand ? { brand } : {}),
    ...(model ? { model } : {}),
    ...(typeof year === "number" ? { modelYear: year } : {}),
    ...(notes ? { notes } : {}),
  };
}

export interface UpdateVehicleBody {
  version: number;
  plate?: string;
  fuelProductId?: string | null;
  connectorType?: VehicleConnectorType | null;
  brand?: string | null;
  model?: string | null;
  modelYear?: number | null;
  notes?: string | null;
}

/** Araç düzenleme: yalnızca değişen alanlar; maskeli plaka gövdeye çıkmaz. */
export function updateVehicleBody(values: VehicleFormValues, vehicle: Vehicle): UpdateVehicleBody {
  const body: UpdateVehicleBody = { version: vehicle.version };
  const plateMasked = vehicle.personalDataMasked || vehicle.plate.includes("*");
  if (!plateMasked && values.plate.trim() !== vehicle.plate) body.plate = values.plate.trim();
  const fuelProductId = values.fuelProductId === "" ? null : values.fuelProductId;
  if (fuelProductId !== vehicle.fuelProductId) body.fuelProductId = fuelProductId;
  const connectorType = values.connectorType === "" ? null : values.connectorType;
  if (connectorType !== vehicle.connectorType) body.connectorType = connectorType;
  const brand = orNull(values.brand);
  if (brand !== vehicle.brand) body.brand = brand;
  const model = orNull(values.model);
  if (model !== vehicle.model) body.model = model;
  const year = parseModelYear(values.modelYear);
  if (year !== undefined && year !== vehicle.modelYear) body.modelYear = year;
  const notes = orNull(values.notes);
  if (notes !== vehicle.notes) body.notes = notes;
  return body;
}

// ── Müşteri 360 ──────────────────────────────────────────────────────────────
export const OVERVIEW_PERIODS = ["30", "90", "365", "1095"] as const;
export type OverviewPeriod = (typeof OVERVIEW_PERIODS)[number];
export const DEFAULT_OVERVIEW_PERIOD: OverviewPeriod = "365";

/** Dönem seçimi → API aralığı: bugün dâhil son N gün (İstanbul günü). Sunucu en çok 3 yıl kabul eder. */
export function overviewRange(period: OverviewPeriod, today: LocalDate): { from: LocalDate; to: LocalDate } {
  return { from: addDays(today, -(Number(period) - 1)), to: today };
}

/** `perMonth` ("1.50") → Türkiye biçimi ("1,50"); float hesabı yapılmaz. */
export function formatPerMonth(perMonth: string): string {
  return Decimal.from(perMonth).formatTr(2);
}

/** Puan (tam sayı dizge) binlik ayırıcıyla: "12500" → "12.500". */
export function formatPoints(points: string): string {
  return Decimal.from(points).formatTr(0);
}

export const KNOWN_BUSINESS_UNITS = ["FUEL", "LPG", "MARKET", "WASH", "EV", "CAFE", "RENTAL"] as const;
export type KnownBusinessUnit = (typeof KNOWN_BUSINESS_UNITS)[number];

export function isKnownBusinessUnit(unit: string | null): unit is KnownBusinessUnit {
  return unit !== null && (KNOWN_BUSINESS_UNITS as readonly string[]).includes(unit);
}

export interface SpendingRow {
  /** İş birimi kodu; bilinmeyen / boş birimler tek "OTHER" satırında toplanır. */
  key: KnownBusinessUnit | "OTHER";
  label: string;
  /** Net harcama (kuruş dizgesi). */
  net: string;
  lineCount: number;
}

/**
 * İş birimi kırılımı → tablo / grafik satırları. Bilinmeyen ya da boş birim "Diğer"de TOPLANIR (kuruş, BigInt);
 * sıra: bilinen birimler sabit sırada (akaryakıt, LPG, market…), en sonda "Diğer". Tutarı sıfır olan satır da kalır
 * (satır sayısı anlamlıdır: iade ile sıfırlanan harcama görünmeli).
 */
export function spendingRows(
  byBusinessUnit: readonly { businessUnit: string | null; net: string; lineCount: number }[],
  labels: { unit: (unit: KnownBusinessUnit) => string; other: string },
): SpendingRow[] {
  const known = new Map<KnownBusinessUnit, { net: bigint; lineCount: number }>();
  const other = { net: 0n, lineCount: 0, present: false };
  for (const item of byBusinessUnit) {
    if (isKnownBusinessUnit(item.businessUnit)) {
      const current = known.get(item.businessUnit) ?? { net: 0n, lineCount: 0 };
      known.set(item.businessUnit, {
        net: current.net + BigInt(item.net),
        lineCount: current.lineCount + item.lineCount,
      });
    } else {
      other.net += BigInt(item.net);
      other.lineCount += item.lineCount;
      other.present = true;
    }
  }
  const rows: SpendingRow[] = KNOWN_BUSINESS_UNITS.filter((unit) => known.has(unit)).map((unit) => ({
    key: unit,
    label: labels.unit(unit),
    net: known.get(unit)!.net.toString(),
    lineCount: known.get(unit)!.lineCount,
  }));
  if (other.present)
    rows.push({ key: "OTHER", label: labels.other, net: other.net.toString(), lineCount: other.lineCount });
  return rows;
}

/** Kuruş dizgesi → grafik için TL sayısı (yalnızca çizim). */
function toMajorNumber(minor: string): number {
  return Number(Money.fromMinor(minor).toMajorString());
}

/** İş birimi kırılımlı net harcama sütun grafiği (tek seri; tablo alternatifi yanında). */
export function spendingChartOption(
  rows: readonly SpendingRow[],
  labels: { net: string; amountAxis: string },
): ChartOption {
  return {
    grid: GRID,
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: formatChartNumber },
    legend: { ...LEGEND_POSITION, data: [labels.net] },
    xAxis: { type: "category", data: rows.map((row) => row.label), axisLabel: { hideOverlap: true } },
    yAxis: { type: "value", name: labels.amountAxis, axisLabel: VALUE_AXIS_LABEL },
    series: [{ type: "bar", name: labels.net, data: rows.map((row) => toMajorNumber(row.net)) }],
  };
}

// ── KVKK: rıza kararları ─────────────────────────────────────────────────────
/** Açık rıza amaç kodunun Türkçe etiketi; önerilen listede yoksa kodun kendisi (kiracı yeni kod açabilir). */
export function purposeLabel(code: string | null): string | null {
  if (code === null) return null;
  return (PRIVACY_CONSENT_PURPOSE_LABELS as Record<string, string>)[code] ?? code;
}

/**
 * Metin türüne uygun kararlar (sunucudaki `decisionAllowed` ile aynı): aydınlatma → yalnızca "okundu"; açık rıza ve
 * ticari ileti → verildi / reddedildi / geri alındı. Yürürlükten kalkmış (`retired`) sürüme YALNIZCA geri alma yazılır;
 * aydınlatmanın geri alınması yoktur.
 */
export function consentDecisionOptions(kind: PrivacyTextKind, retired: boolean): ConsentDecision[] {
  if (kind === "PRIVACY_NOTICE") return retired ? [] : ["ACKNOWLEDGED"];
  return retired ? ["WITHDRAWN"] : ["GRANTED", "DENIED", "WITHDRAWN"];
}

export interface ConsentTextChoice {
  id: string;
  kind: PrivacyTextKind;
  purposeCode: string | null;
  title: string;
  revision: number;
  /** Yürürlükten kalkmış sürüm (yalnızca müşterinin geçmişinden gelir; tek karar: geri alma). */
  retired: boolean;
}

/**
 * Karar penceresindeki metin seçenekleri: YAYIMDAKİ sürümler + müşterinin geçmişinde olup artık yayımda olmayan
 * (yürürlükten kalkmış) rıza / ileti metinleri — onlara verilmiş rıza da geri alınabilmeli (privacy.md).
 */
export function consentTextChoices(
  published: readonly PrivacyText[],
  history: readonly ConsentRecord[],
): ConsentTextChoice[] {
  const choices: ConsentTextChoice[] = published.map((text) => ({
    id: text.id,
    kind: text.kind,
    purposeCode: text.purposeCode,
    title: text.title,
    revision: text.revision,
    retired: false,
  }));
  const seen = new Set(choices.map((choice) => choice.id));
  for (const record of history) {
    if (seen.has(record.privacyTextId) || record.kind === "PRIVACY_NOTICE") continue;
    seen.add(record.privacyTextId);
    choices.push({
      id: record.privacyTextId,
      kind: record.kind,
      purposeCode: record.purposeCode,
      title: record.textTitle,
      revision: record.textRevision,
      retired: true,
    });
  }
  return choices;
}

export interface PublishedTextGroups {
  notice: PrivacyText | null;
  explicit: PrivacyText[];
  commercial: PrivacyText | null;
}

/** Yayımdaki metinler türe göre (tür + amaç başına en çok bir yayımda sürüm vardır). */
export function publishedTextGroups(texts: readonly PrivacyText[]): PublishedTextGroups {
  const published = texts.filter((text) => text.status === "PUBLISHED");
  return {
    notice: published.find((text) => text.kind === "PRIVACY_NOTICE") ?? null,
    explicit: published
      .filter((text) => text.kind === "EXPLICIT_CONSENT")
      .sort((left, right) => (left.purposeCode ?? "").localeCompare(right.purposeCode ?? "")),
    commercial: published.find((text) => text.kind === "COMMERCIAL_MESSAGE") ?? null,
  };
}

export interface InitialConsentInput {
  channel: ConsentChannel;
  noticeAcknowledged: boolean;
  /** İşaretlenen açık rıza metinleri. */
  grantedConsentTextIds: readonly string[];
  /** İşaretlenen ticari ileti kanalları. */
  messageChannels: readonly MessageChannel[];
}

export interface ConsentRequestBody {
  privacyTextId: string;
  decision: ConsentDecision;
  channel: ConsentChannel;
}

export interface MessagePermissionRequestBody {
  channel: MessageChannel;
  decision: "GRANTED";
  sourceChannel: ConsentChannel;
  privacyTextId: string | null;
}

/**
 * Yeni müşteride İLK rıza kayıtları: aydınlatma "okundu" (yayımdaki aydınlatma) + işaretlenen her yayımdaki açık rıza
 * metni için "verildi". Yayımda olmayan / işaretlenmeyen metne kayıt ÜRETİLMEZ.
 */
export function initialConsentRequests(
  texts: readonly PrivacyText[],
  input: InitialConsentInput,
): ConsentRequestBody[] {
  const groups = publishedTextGroups(texts);
  const requests: ConsentRequestBody[] = [];
  if (input.noticeAcknowledged && groups.notice)
    requests.push({ privacyTextId: groups.notice.id, decision: "ACKNOWLEDGED", channel: input.channel });
  for (const text of groups.explicit)
    if (input.grantedConsentTextIds.includes(text.id))
      requests.push({ privacyTextId: text.id, decision: "GRANTED", channel: input.channel });
  return requests;
}

/**
 * İlk ticari ileti izinleri (kanal başına "verildi"). İzin VERME yayımdaki ticari ileti metnine bağlıdır: yayımda metin
 * yoksa istek ÜRETİLMEZ (sunucu 409 verirdi; ekran kutuları da çizmez).
 */
export function initialPermissionRequests(
  texts: readonly PrivacyText[],
  input: InitialConsentInput,
): MessagePermissionRequestBody[] {
  const commercial = publishedTextGroups(texts).commercial;
  if (commercial === null) return [];
  return MESSAGE_CHANNELS.filter((channel) => input.messageChannels.includes(channel)).map((channel) => ({
    channel,
    decision: "GRANTED",
    sourceChannel: input.channel,
    privacyTextId: commercial.id,
  }));
}

// ── Bağlantı daveti ──────────────────────────────────────────────────────────
/** Davetin kalan süresi "dd:ss" (ör. "14:05"); süresi dolduysa `null`. */
export function remainingTime(expiresAt: string, nowMs: number): string | null {
  const remainingMs = Date.parse(expiresAt) - nowMs;
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return null;
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
